import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import PixRoundedIcon from "@mui/icons-material/PixRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { getSalesEventsSummary } from "../Reports/reportApi";
import {
  buildDateRange,
  getConfirmedBuyerCount,
  getExitReasonLabel,
  getPreviousStepRate,
  getValidationErrorLabel,
  PERIOD_OPTIONS,
} from "./paymentOperationsUtils";

const SURFACE = "rgba(22, 34, 56, 0.94)";
const SOFT_SURFACE = "rgba(13, 27, 42, 0.58)";
const BORDER = "rgba(160, 174, 192, 0.17)";

const queueStyles = {
  needs_action: {
    accent: "#ff7b86",
    background: "rgba(244, 67, 54, 0.08)",
    border: "rgba(244, 67, 54, 0.28)",
    badge: "PRECISA DE AÇÃO",
    icon: <ErrorOutlineRoundedIcon />,
  },
  awaiting_payment: {
    accent: "#ffca63",
    background: "rgba(255, 183, 77, 0.07)",
    border: "rgba(255, 183, 77, 0.25)",
    badge: "AGUARDANDO",
    icon: <HourglassTopRoundedIcon />,
  },
  worth_contacting: {
    accent: "#55dff7",
    background: "rgba(0, 212, 255, 0.07)",
    border: "rgba(0, 212, 255, 0.23)",
    badge: "VALE CHAMAR",
    icon: <PersonSearchRoundedIcon />,
  },
};

const emptyFollowUp = {
  needs_action: [],
  awaiting_payment: [],
  worth_contacting: [],
  counts: { needs_action: 0, awaiting_payment: 0, worth_contacting: 0 },
};

const formatWhen = (value) => {
  if (!value) return "Horário não registrado";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Horário não registrado";
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  const time = new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  if (sameDay) return `Hoje às ${time}`;
  const day = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  }).format(date);
  return `${day} às ${time}`;
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const SectionCard = ({ children, sx = {}, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.75, sm: 2.25 },
      bgcolor: SURFACE,
      border: `1px solid ${BORDER}`,
      borderRadius: "18px",
      boxShadow: "0 14px 36px rgba(0, 0, 0, 0.14)",
      ...sx,
    }}
    {...props}
  >
    {children}
  </Paper>
);

const QueueCount = ({ value, label, tone }) => {
  const style = queueStyles[tone];
  return (
    <Box
      sx={{
        minWidth: 0,
        px: 1.1,
        py: 1,
        borderRadius: "12px",
        bgcolor: style.background,
        border: `1px solid ${style.border}`,
      }}
    >
      <Typography sx={{ color: style.accent, fontSize: "1.15rem", fontWeight: 900, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography sx={{ mt: 0.45, color: "text.secondary", fontSize: "0.65rem", lineHeight: 1.25 }}>
        {label}
      </Typography>
    </Box>
  );
};

const PersonCard = ({ person, queue }) => {
  const style = queueStyles[queue];
  const cleanPhone = String(person.phone || "").replace(/\D/g, "");
  const whatsappPhone = cleanPhone.startsWith("55") ? cleanPhone : `55${cleanPhone}`;
  const firstName = String(person.full_name || "").trim().split(/\s+/)[0] || "Olá";
  const whatsappText = encodeURIComponent(
    queue === "awaiting_payment"
      ? `Olá, ${firstName}! Vi que seu Pix ainda está aguardando pagamento. Ficou alguma dúvida? Posso ajudar.`
      : `Olá, ${firstName}! Vi que você tentou fazer o pagamento e quero ajudar. Ficou alguma dúvida ou apareceu algum erro?`
  );
  const technicalDetails = [
    person.technical_code ? `Código ${person.technical_code}` : null,
    person.http_status ? `HTTP ${person.http_status}` : null,
    person.device_class === "mobile"
      ? "celular"
      : person.device_class === "desktop"
        ? "computador"
        : null,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        p: { xs: 1.35, sm: 1.55 },
        minWidth: 0,
        borderRadius: "15px",
        bgcolor: style.background,
        border: `1px solid ${style.border}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.1 }}>
        <Avatar
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            bgcolor: "rgba(255,255,255,.06)",
            color: style.accent,
            border: `1px solid ${style.border}`,
            fontSize: "0.78rem",
            fontWeight: 900,
          }}
        >
          {String(person.full_name || "?").trim().charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.65 }}>
            <Typography sx={{ minWidth: 0, fontSize: "0.85rem", fontWeight: 850 }}>
              {person.full_name || "Nome não informado"}
            </Typography>
            <Box
              component="span"
              sx={{
                px: 0.7,
                py: 0.27,
                borderRadius: 20,
                color: style.accent,
                bgcolor: "rgba(0,0,0,.12)",
                fontSize: "0.56rem",
                fontWeight: 900,
                letterSpacing: 0.4,
              }}
            >
              {person.reminder_recommended ? "LEMBRETE RECOMENDADO" : style.badge}
            </Box>
          </Box>
          <Typography sx={{ mt: 0.35, color: "text.secondary", fontSize: "0.67rem" }}>
            {formatWhen(person.occurred_at || person.last_activity_at)}
            {person.method ? ` · ${person.method === "pix" ? "Pix" : "Cartão"}` : ""}
            {person.amount ? ` · ${formatMoney(person.amount)}` : ""}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1.2, pl: { sm: 6.1 } }}>
        <Typography sx={{ fontSize: "0.83rem", fontWeight: 850, lineHeight: 1.35 }}>
          {person.title}
        </Typography>
        <Typography sx={{ mt: 0.35, color: "text.secondary", fontSize: "0.72rem", lineHeight: 1.5 }}>
          {person.description}
        </Typography>
        <Box sx={{ mt: 0.95, p: 0.95, borderRadius: "10px", bgcolor: "rgba(0,0,0,.13)" }}>
          <Typography sx={{ color: style.accent, fontSize: "0.6rem", fontWeight: 900, letterSpacing: 0.35 }}>
            O QUE FAZER
          </Typography>
          <Typography sx={{ mt: 0.25, fontSize: "0.7rem", lineHeight: 1.45 }}>
            {person.recommended_action}
          </Typography>
        </Box>

        {technicalDetails.length > 0 && (
          <Box
            component="details"
            sx={{
              mt: 0.75,
              color: "text.secondary",
              "& summary": { cursor: "pointer", fontSize: "0.65rem" },
            }}
          >
            <Box component="summary">Ver detalhe técnico</Box>
            <Typography sx={{ mt: 0.5, fontSize: "0.63rem" }}>
              {technicalDetails.join(" · ")}
            </Typography>
          </Box>
        )}

        {cleanPhone && (
          <Button
            component="a"
            href={`https://wa.me/${whatsappPhone}?text=${whatsappText}`}
            target="_blank"
            rel="noreferrer"
            startIcon={<WhatsAppIcon />}
            fullWidth
            sx={{
              mt: 1.1,
              minHeight: 42,
              color: "#08251a",
              bgcolor: "#66d69a",
              borderRadius: "11px",
              fontSize: "0.71rem",
              fontWeight: 900,
              "&:hover": { bgcolor: "#78e3aa", transform: "none", boxShadow: "none" },
            }}
          >
            {queue === "awaiting_payment" && person.reminder_recommended
              ? "Enviar lembrete no WhatsApp"
              : "Ajudar pelo WhatsApp"}
          </Button>
        )}
      </Box>
    </Box>
  );
};

const PeopleSection = ({ title, description, queue, people }) => {
  if (!people.length) return null;
  const style = queueStyles[queue];
  return (
    <SectionCard component="section">
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.95, mb: 1.3 }}>
        <Box sx={{ mt: 0.1, color: style.accent, display: "grid", placeItems: "center" }}>
          {style.icon}
        </Box>
        <Box>
          <Typography component="h2" sx={{ fontSize: "0.97rem", fontWeight: 850 }}>
            {title}
          </Typography>
          <Typography sx={{ mt: 0.2, color: "text.secondary", fontSize: "0.71rem", lineHeight: 1.4 }}>
            {description}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
        {people.map((person) => (
          <PersonCard key={person.user_id} person={person} queue={queue} />
        ))}
      </Box>
    </SectionCard>
  );
};

const FunnelStep = ({ label, value, previous, maxValue, isLast }) => {
  const rate = getPreviousStepRate(value, previous);
  const width = maxValue > 0 ? Math.max(6, Math.round((value / maxValue) * 100)) : 0;
  return (
    <Box sx={{ mb: 1.3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ fontSize: "0.71rem", fontWeight: 750 }}>{label}</Typography>
        <Typography sx={{ fontSize: "0.71rem", fontWeight: 900 }}>{value}</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={width}
        sx={{
          mt: 0.5,
          height: 5,
          borderRadius: 10,
          bgcolor: "rgba(160,174,192,.12)",
          "& .MuiLinearProgress-bar": {
            borderRadius: 10,
            bgcolor: isLast ? "#66d69a" : "#55dff7",
          },
        }}
      />
      {rate !== null && previous > 0 && (
        <Typography sx={{ mt: 0.3, color: "text.secondary", fontSize: "0.6rem" }}>
          {rate}% em relação à etapa anterior
        </Typography>
      )}
    </Box>
  );
};

const DashboardSkeleton = () => (
  <Stack spacing={1.5} aria-label="Carregando visão de vendas">
    <Skeleton variant="rounded" height={150} sx={{ borderRadius: "18px" }} />
    <Skeleton variant="rounded" height={250} sx={{ borderRadius: "18px" }} />
  </Stack>
);

const PaymentOperationsDashboard = () => {
  const [period, setPeriod] = useState(7);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);

  const loadSummary = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      setSummary(await getSalesEventsSummary(buildDateRange(period)));
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error ||
          "Não foi possível carregar os dados agora. Tente novamente."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [period]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const followUp = summary?.acompanhamento_pagamentos || emptyFollowUp;
  const followUpCounts = followUp.counts || emptyFollowUp.counts;
  const totalFollowUp =
    Number(followUpCounts.needs_action || 0) +
    Number(followUpCounts.awaiting_payment || 0) +
    Number(followUpCounts.worth_contacting || 0);
  const confirmedBuyers = getConfirmedBuyerCount(summary || {});
  const periodHeading = period === 1 ? "hoje" : `nos últimos ${period} dias`;
  const funnelSteps = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Viram a oferta", value: Number(summary.leads_com_offer_view) || 0 },
      { label: "Clicaram para assinar", value: Number(summary.leads_com_cta_click) || 0 },
      { label: "Abriram o pagamento", value: Number(summary.leads_com_payment_view) || 0 },
      { label: "Escolheram como pagar", value: Number(summary.leads_com_metodo_selecionado) || 0 },
      { label: "Geraram uma cobrança", value: Number(summary.leads_com_cobranca_gerada) || 0 },
      { label: "Pagaram", value: confirmedBuyers },
    ];
  }, [confirmedBuyers, summary]);
  const maxFunnelValue = Math.max(0, ...funnelSteps.map((step) => step.value));
  const exitReasons = summary?.motivos_saida || [];
  const validationErrors = summary?.erros_validacao || [];

  return (
    <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", px: { xs: 1.25, sm: 2 }, pb: 5 }}>
      <Box component="header" sx={{ display: { sm: "flex" }, alignItems: "flex-end", justifyContent: "space-between", gap: 2, mb: 2 }}>
        <Box>
          <Typography sx={{ color: "primary.main", fontSize: "0.65rem", fontWeight: 900, letterSpacing: 1 }}>
            CENTRAL DO ADMIN
          </Typography>
          <Typography component="h1" sx={{ mt: 0.3, fontSize: { xs: "1.4rem", sm: "1.8rem" }, fontWeight: 850 }}>
            Vendas e pagamentos
          </Typography>
          <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: { xs: "0.75rem", sm: "0.82rem" } }}>
            Saiba quem teve problema, quem está aguardando e quem vale chamar.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mt: { xs: 1.5, sm: 0 } }}>
          <Box role="group" aria-label="Período do painel" sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", flex: 1, minWidth: { sm: 250 }, p: 0.4, border: `1px solid ${BORDER}`, bgcolor: SURFACE, borderRadius: "13px" }}>
            {PERIOD_OPTIONS.map((option) => (
              <Button
                key={option.value}
                onClick={() => setPeriod(option.value)}
                aria-pressed={period === option.value}
                sx={{
                  minWidth: 0,
                  minHeight: 38,
                  px: 1,
                  borderRadius: "9px",
                  fontSize: "0.7rem",
                  color: period === option.value ? "#071927" : "text.secondary",
                  bgcolor: period === option.value ? "primary.main" : "transparent",
                  boxShadow: "none",
                  "&:hover": { transform: "none", boxShadow: "none", bgcolor: period === option.value ? "#31ddff" : "rgba(255,255,255,.05)" },
                }}
              >
                {option.label}
              </Button>
            ))}
          </Box>
          <IconButton
            onClick={() => loadSummary({ silent: true })}
            disabled={refreshing || loading}
            aria-label="Atualizar dados"
            sx={{ width: 46, height: 46, flexShrink: 0, color: "primary.main", border: `1px solid ${BORDER}`, bgcolor: SURFACE, borderRadius: "13px" }}
          >
            {refreshing ? <CircularProgress size={19} /> : <RefreshRoundedIcon />}
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => loadSummary()}>Tentar novamente</Button>} sx={{ mb: 2, borderRadius: "14px" }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <DashboardSkeleton />
      ) : summary ? (
        <Stack spacing={1.35}>
          <SectionCard component="section" sx={{ background: "linear-gradient(145deg, rgba(23,48,72,.98), rgba(22,34,56,.94))", borderColor: "rgba(0,212,255,.2)" }}>
            <Box sx={{ display: { sm: "flex" }, alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box>
                <Typography sx={{ color: "text.secondary", fontSize: "0.65rem", fontWeight: 750 }}>
                  O QUE MERECE ATENÇÃO {periodHeading.toUpperCase()}
                </Typography>
                <Typography sx={{ mt: 0.45, fontSize: { xs: "1.05rem", sm: "1.25rem" }, fontWeight: 900 }}>
                  {totalFollowUp === 0
                    ? "Nenhuma pessoa precisa de acompanhamento agora"
                    : `${totalFollowUp} ${totalFollowUp === 1 ? "pessoa para acompanhar" : "pessoas para acompanhar"}`}
                </Typography>
                <Typography sx={{ mt: 0.3, color: "text.secondary", fontSize: "0.71rem" }}>
                  Cada pessoa aparece uma vez, conforme a situação mais recente.
                </Typography>
              </Box>
              <Box sx={{ mt: { xs: 1.35, sm: 0 }, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 0.65, minWidth: { sm: 330 } }}>
                <QueueCount value={followUpCounts.needs_action || 0} label="precisa de ação" tone="needs_action" />
                <QueueCount value={followUpCounts.awaiting_payment || 0} label="aguardando" tone="awaiting_payment" />
                <QueueCount value={followUpCounts.worth_contacting || 0} label="vale chamar" tone="worth_contacting" />
              </Box>
            </Box>
          </SectionCard>

          <PeopleSection title="Precisa de ação agora" description="A tentativa mais recente terminou em erro ou precisa de correção." queue="needs_action" people={followUp.needs_action || []} />
          <PeopleSection title="Aguardando pagamento" description="A cobrança existe e está válida. Isso não é erro." queue="awaiting_payment" people={followUp.awaiting_payment || []} />
          <PeopleSection title="Vale chamar" description="Demonstrou interesse ou pediu ajuda, mas não há cobrança válida agora." queue="worth_contacting" people={followUp.worth_contacting || []} />

          {totalFollowUp === 0 && (
            <SectionCard component="section" sx={{ py: 3, textAlign: "center" }}>
              <CheckCircleRoundedIcon sx={{ color: "#66d69a", fontSize: 34 }} />
              <Typography sx={{ mt: 0.6, fontSize: "0.9rem", fontWeight: 850 }}>Tudo resolvido por enquanto</Typography>
              <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: "0.71rem" }}>
                Não encontramos erro, cobrança pendente ou pessoa esperando ajuda neste período.
              </Typography>
            </SectionCard>
          )}

          <SectionCard component="section" sx={{ p: 0, overflow: "hidden" }}>
            <Button
              fullWidth
              onClick={() => setDetailsOpen((open) => !open)}
              aria-expanded={detailsOpen}
              endIcon={<ExpandMoreRoundedIcon sx={{ transform: detailsOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }} />}
              sx={{ minHeight: 66, px: 2, justifyContent: "space-between", color: "text.primary", borderRadius: 0, "&:hover": { transform: "none", boxShadow: "none", bgcolor: "rgba(255,255,255,.03)" } }}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography sx={{ fontSize: "0.79rem", fontWeight: 850 }}>Entender o caminho até a compra</Typography>
                <Typography sx={{ mt: 0.15, color: "text.secondary", fontSize: "0.64rem", fontWeight: 500 }}>
                  {confirmedBuyers} {confirmedBuyers === 1 ? "venda confirmada" : "vendas confirmadas"} · etapas, Pix, cartão e saídas
                </Typography>
              </Box>
            </Button>
            <Collapse in={detailsOpen}>
              <Box sx={{ px: { xs: 1.75, sm: 2.25 }, pb: 2.25 }}>
                <Alert severity="info" sx={{ mb: 2, borderRadius: "12px", fontSize: "0.69rem" }}>
                  Esta é uma visão aberta: algumas pessoas entram direto no pagamento e podem pular etapas.
                </Alert>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.2fr) minmax(280px, .8fr)" }, gap: 2.5 }}>
                  <Box>
                    <Typography sx={{ mb: 1.2, fontSize: "0.81rem", fontWeight: 850 }}>Etapas registradas</Typography>
                    {funnelSteps.map((step, index) => (
                      <FunnelStep key={step.label} label={step.label} value={step.value} previous={index > 0 ? funnelSteps[index - 1].value : null} maxValue={maxFunnelValue} isLast={index === funnelSteps.length - 1} />
                    ))}
                  </Box>
                  <Stack spacing={2.1}>
                    <Box>
                      <Typography sx={{ mb: 0.9, fontSize: "0.79rem", fontWeight: 850 }}>Forma escolhida</Typography>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 0.8 }}>
                        <Box sx={{ p: 1.15, borderRadius: "12px", bgcolor: SOFT_SURFACE, border: `1px solid ${BORDER}` }}>
                          <PixRoundedIcon sx={{ color: "#55dff7", fontSize: 20 }} />
                          <Typography sx={{ mt: 0.4, fontSize: "1.12rem", fontWeight: 900 }}>{summary.leads_com_pix_start || 0}</Typography>
                          <Typography sx={{ color: "text.secondary", fontSize: "0.63rem" }}>começaram no Pix</Typography>
                        </Box>
                        <Box sx={{ p: 1.15, borderRadius: "12px", bgcolor: SOFT_SURFACE, border: `1px solid ${BORDER}` }}>
                          <CreditCardRoundedIcon sx={{ color: "#8ea9ff", fontSize: 20 }} />
                          <Typography sx={{ mt: 0.4, fontSize: "1.12rem", fontWeight: 900 }}>{summary.leads_com_card_start || 0}</Typography>
                          <Typography sx={{ color: "text.secondary", fontSize: "0.63rem" }}>começaram no cartão</Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box>
                      <Typography sx={{ mb: 0.7, fontSize: "0.75rem", fontWeight: 850 }}>Por que disseram que saíram</Typography>
                      {exitReasons.length === 0 ? (
                        <Typography sx={{ color: "text.secondary", fontSize: "0.69rem" }}>Ninguém informou um motivo.</Typography>
                      ) : exitReasons.map((reason) => (
                        <Box key={reason.reason} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.3 }}>
                          <Typography sx={{ color: "text.secondary", fontSize: "0.68rem" }}>{getExitReasonLabel(reason.reason)}</Typography>
                          <Typography sx={{ fontSize: "0.68rem", fontWeight: 850 }}>{reason.total_users}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {validationErrors.length > 0 && (
                      <Box>
                        <Typography sx={{ mb: 0.7, fontSize: "0.75rem", fontWeight: 850 }}>Erros de preenchimento</Typography>
                        {validationErrors.map((item, index) => (
                          <Box key={`${item.method}-${item.field}-${item.code}-${index}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1, py: 0.3 }}>
                            <Typography sx={{ color: "text.secondary", fontSize: "0.68rem" }}>{getValidationErrorLabel(item)}</Typography>
                            <Typography sx={{ fontSize: "0.68rem", fontWeight: 850 }}>{item.total_users}</Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Collapse>
          </SectionCard>
        </Stack>
      ) : null}
    </Box>
  );
};

export default PaymentOperationsDashboard;
